import { produce } from 'immer'

import { EventActions } from '../actions'
import { ApiError } from '../api-error'
import type { FloorplanData, Seat } from '../components/floorplan/floorplan'
import { handleError } from '../handle-error'
import type { FloorplanSectionProps } from '../modules/checkout/floorplan-section'
import type { EventView, TicketGroup, UnavailableSeat } from '../types'

const findSeat = (data: FloorplanData, rowNumber: number, seatNumber: number): Seat | null => {
    for (const grid of data.grids) {
        for (const row of grid.rows) {
            if (row.number !== rowNumber) {
                continue
            }

            for (const seat of row.seats) {
                if (seat?.number === seatNumber) {
                    return seat
                }
            }
        }
    }

    return null
}

const getFloorplanData = (
    floorplan: FloorplanData,
    ticketGroups: TicketGroup[],
    unavailableSeats: UnavailableSeat[],
): FloorplanData => {
    // Use immer to create a copy instead of modifying the base floorplan in state
    return produce(floorplan, (draft) => {
        for (const group of ticketGroups) {
            for (const seat of group.seats ?? []) {
                const floorplanSeat = findSeat(draft, seat.rowNumber, seat.seatNumber)

                if (floorplanSeat) {
                    floorplanSeat.isBooked = true
                } else {
                    console.warn('Seat not found:', seat.rowNumber, seat.seatNumber)
                }
            }
        }

        for (const seat of unavailableSeats) {
            const floorplanSeat = findSeat(draft, seat.row, seat.seat)

            if (floorplanSeat) {
                if (seat.status === 'taken') {
                    floorplanSeat.isTaken = true
                } else if (seat.status === 'disabled') {
                    floorplanSeat.isDisabled = true
                }
            } else {
                console.warn('Seat not found:', seat.row, seat.seat)
            }
        }
    })
}

export const getFloorplanSection = (
    view: EventView,
    floorplan: FloorplanData,
): FloorplanSectionProps => {
    const { state, update } = view
    const { lang } = state

    const floorplanData = getFloorplanData(floorplan, state.calc.tickets, state.seats.unavailable)

    return {
        title: state.labels.intro.title,
        text: state.labels.intro.text,
        plan: {
            data: floorplanData,
            texts: {
                screen: lang.floorplan_screen,
                entrance: lang.floorplan_entry,
                stage: lang.floorplan_stage,
                balcony: lang.floorplan_balcony,
                sideBalcony: lang.floorplan_sideBalcony,
            },
            onClickSeat: async (rowNumber, seatNumber) => {
                const floorplanSeat = findSeat(floorplanData, rowNumber, seatNumber)

                if (floorplanSeat && !floorplanSeat.isTaken && !floorplanSeat.isDisabled) {
                    try {
                        if (floorplanSeat.isBooked) {
                            await EventActions.removeSeat(view, rowNumber, seatNumber)
                            state.isTicketLimit = false
                            update()
                        } else {
                            await EventActions.addSeat(view, rowNumber, seatNumber)
                        }
                    } catch (error) {
                        const errorCode = error instanceof ApiError ? error.code : null

                        if (errorCode === 'maximum-tickets-per-session') {
                            state.isTicketLimit = true
                            update()
                        } else if (errorCode === 'unavailable-seat') {
                            // TODO avoid full refresh, just reload data?
                            window.location.reload()
                        } else {
                            handleError(view, error as Error)
                        }
                    }
                }
            },
        },
    }
}
